import React, { useState, useEffect, useRef } from 'react';

/**
 * InternalNoteModal - Modal för att redigera intern förklaring för ett arbetsmoment
 * 
 * @param {boolean} isOpen - Om modalen är öppen
 * @param {Function} onClose - Callback för att stänga modalen
 * @param {Function} onSave - Callback för att spara förklaringen (note) => void
 * @param {Function} onDelete - Callback för att ta bort förklaringen
 * @param {string} itemLabel - Label för arbetsmomentet
 * @param {string} currentNote - Nuvarande förklaring
 */
const InternalNoteModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  itemLabel,
  currentNote = ''
}) => {
  const [note, setNote] = useState(currentNote);
  const textareaRef = useRef(null);

  // Uppdatera note när currentNote ändras (när modal öppnas för annan item)
  useEffect(() => {
    setNote(currentNote);
  }, [currentNote]);

  // Fokusera textarea när modal öppnas
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Hantera ESC-tangent
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSave = () => {
    onSave(note.trim());
    onClose();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    onClose();
  };

  if (!isOpen) return null;

  const hasExistingNote = currentNote && currentNote.trim().length > 0;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="internal-note-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 
            id="internal-note-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Intern förklaring
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            För: <span className="font-medium text-gray-700">{itemLabel}</span>
          </p>
        </div>
        
        {/* Content */}
        <div className="px-6 py-4">
          <label htmlFor="internal-note-textarea" className="sr-only">
            Intern förklaring
          </label>
          <textarea
            ref={textareaRef}
            id="internal-note-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Skriv en intern förklaring för detta moment. Denna text syns endast för byggkonsulter, inte för kunden."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink resize-none text-sm"
            aria-describedby="internal-note-help"
          />
          <p id="internal-note-help" className="mt-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5">
                <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm.75-10.25a.75.75 0 00-1.5 0v4.69L5.03 7.22a.75.75 0 00-1.06 1.06l2.5 2.5a.75.75 0 001.06 0l2.5-2.5a.75.75 0 10-1.06-1.06L7.75 9.44V4.75z" clipRule="evenodd" />
              </svg>
              Denna förklaring visas endast internt för byggkonsulter
            </span>
          </p>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            {hasExistingNote && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-300"
                aria-label="Ta bort förklaring"
              >
                Ta bort
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Avbryt
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-leadme-pink hover:bg-leadme-pink-dark rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:ring-offset-2"
            >
              Spara
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalNoteModal;

